import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSession } from 'gunsi-auth'
import * as api from '../api.js'

const LS_KEY = 'gunsi_even_data_v1'
const LS_MIGRATED = 'gunsi_even_migrated_db_v1'

const EvenCtx = createContext(null)

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return Date.now().toString(36) + Math.random().toString(36).slice(2)
}

function readLS() {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function writeLS(state) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)) } catch {}
}

const EMPTY = { currency: 'EUR', dateFormat: 'auto', groups: [], expenses: [], settlements: [], tallySync: {}, tallyExcluded: {}, tallyAccess: false }

export function EvenProvider({ children }) {
  const { session, loading: sessionLoading } = useSession()
  const [state, setState] = useState(() => readLS() ?? EMPTY)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const hasLoadedRef = useRef(false)

  // Initial load: pull from backend if logged in, fallback to LS only.
  useEffect(() => {
    if (sessionLoading) return
    if (!session) {
      setLoading(false)
      return
    }
    if (hasLoadedRef.current) return
    hasLoadedRef.current = true

    let cancelled = false
    ;(async () => {
      try {
        const data = await api.loadData()
        if (cancelled) return

        // Migration: if backend empty and LS has data → upload LS.
        const backendEmpty = !data
          || ((data.groups?.length ?? 0) === 0
              && (data.expenses?.length ?? 0) === 0
              && (data.settlements?.length ?? 0) === 0)
        const ls = readLS()
        const lsHasData = ls && ((ls.groups?.length ?? 0) > 0 || (ls.expenses?.length ?? 0) > 0)
        const migrated = localStorage.getItem(LS_MIGRATED)

        if (backendEmpty && lsHasData && !migrated) {
          await api.replaceAllData({
            groups: ls.groups ?? [],
            expenses: ls.expenses ?? [],
            settlements: ls.settlements ?? [],
          })
          if (ls.currency && ls.currency !== data?.currency) {
            await api.saveSettings({ currency: ls.currency })
          }
          localStorage.setItem(LS_MIGRATED, '1')
          setState({
            currency: ls.currency ?? 'EUR',
            dateFormat: data?.dateFormat ?? 'auto',
            groups: ls.groups ?? [],
            expenses: ls.expenses ?? [],
            settlements: ls.settlements ?? [],
            tallySync: data?.tallySync ?? {},
            tallyExcluded: data?.tallyExcluded ?? {},
            tallyAccess: Boolean(data?.tallyAccess),
          })
        } else {
          setState({
            currency: data?.currency ?? 'EUR',
            dateFormat: data?.dateFormat ?? 'auto',
            groups: data?.groups ?? [],
            expenses: data?.expenses ?? [],
            settlements: data?.settlements ?? [],
            tallySync: data?.tallySync ?? {},
            tallyExcluded: data?.tallyExcluded ?? {},
            tallyAccess: Boolean(data?.tallyAccess),
          })
        }
      } catch (err) {
        if (cancelled) return
        // Fall back to LS as offline mirror
        setError(err.message)
        const ls = readLS() ?? EMPTY
        setState(ls)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => { cancelled = true }
  }, [session, sessionLoading])

  // Mirror to LS on every state change
  useEffect(() => { writeLS(state) }, [state])

  // Re-pull dal backend: i gruppi sono condivisi, altri membri possono aver
  // aggiunto/modificato entries. Last-write-wins, niente merge.
  const refresh = useCallback(async () => {
    if (!session) return
    try {
      const data = await api.loadData()
      setState(s => ({
        ...s,
        currency: data?.currency ?? s.currency,
        dateFormat: data?.dateFormat ?? s.dateFormat ?? 'auto',
        groups: data?.groups ?? [],
        expenses: data?.expenses ?? [],
        settlements: data?.settlements ?? [],
        tallySync: data?.tallySync ?? s.tallySync ?? {},
        tallyExcluded: data?.tallyExcluded ?? s.tallyExcluded ?? {},
        tallyAccess: Boolean(data?.tallyAccess),
      }))
    } catch (e) { console.warn('refresh', e) }
  }, [session])

  // Refetch quando la finestra torna in focus (throttle 15s)
  const lastRefreshRef = useRef(0)
  useEffect(() => {
    if (!session) return
    const maybeRefresh = () => {
      if (document.visibilityState === 'hidden') return
      const now = Date.now()
      if (now - lastRefreshRef.current < 15000) return
      lastRefreshRef.current = now
      refresh()
    }
    window.addEventListener('focus', maybeRefresh)
    document.addEventListener('visibilitychange', maybeRefresh)
    return () => {
      window.removeEventListener('focus', maybeRefresh)
      document.removeEventListener('visibilitychange', maybeRefresh)
    }
  }, [session, refresh])

  // ── Mutations (optimistic + per-mutation sync) ────────────────────────────

  const setCurrency = useCallback(async (currency) => {
    setState(s => ({ ...s, currency }))
    if (session) {
      try { await api.saveSettings({ currency }) } catch (e) { console.warn('saveSettings', e) }
    }
  }, [session])

  const setDateFormat = useCallback(async (dateFormat) => {
    setState(s => ({ ...s, dateFormat }))
    if (session) {
      try { await api.saveSettings({ dateFormat }) } catch (e) { console.warn('saveSettings', e) }
    }
  }, [session])

  const createGroup = useCallback(async ({ name, emoji, currency, members }) => {
    const now = new Date().toISOString()
    const group = {
      id: uuid(),
      name,
      emoji: emoji || '👥',
      currency: currency || state.currency || 'EUR',
      members: (members ?? []).map(m => ({
        id: m.id || uuid(),
        name: m.name,
        userId: m.userId ?? null,
        isYou: !!m.isYou,
      })),
      createdAt: now,
      updatedAt: now,
    }
    setState(s => ({ ...s, groups: [group, ...s.groups] }))
    if (session) {
      // Reconcile: il server calcola ownerId/memberUserIds e normalizza i members
      try {
        const saved = await api.createGroup(group)
        if (saved) setState(s => ({ ...s, groups: s.groups.map(g => g.id === group.id ? { ...g, ...saved } : g) }))
      } catch (e) { console.warn('createGroup', e) }
    }
    return group
  }, [session, state.currency])

  const updateGroup = useCallback(async (id, patch) => {
    const now = new Date().toISOString()
    let before = null
    setState(s => {
      before = s.groups.find(g => g.id === id) ?? null
      return {
        ...s,
        groups: s.groups.map(g => g.id === id ? { ...g, ...patch, updatedAt: now } : g),
      }
    })
    if (session) {
      try {
        const saved = await api.patchGroup(id, { ...patch, updatedAt: now })
        if (saved) setState(s => ({ ...s, groups: s.groups.map(g => g.id === id ? { ...g, ...saved } : g) }))
      } catch (e) {
        console.warn('patchGroup', e)
        // Rifiutato dal server (es. membro con spese): riallinea lo state
        // all'ultimo stato valido e rilancia, così il form può avvisare
        if (e?.code) {
          setState(s => ({ ...s, groups: s.groups.map(g => g.id === id ? { ...g, ...before } : g) }))
          throw e
        }
      }
    }
  }, [session])

  const removeGroup = useCallback(async (id) => {
    setState(s => ({
      ...s,
      groups: s.groups.filter(g => g.id !== id),
      expenses: s.expenses.filter(e => e.groupId !== id),
      settlements: s.settlements.filter(x => x.groupId !== id),
    }))
    if (session) {
      try { await api.deleteGroup(id) } catch (e) { console.warn('deleteGroup', e) }
    }
  }, [session])

  const addExpense = useCallback(async (expense) => {
    const now = new Date().toISOString()
    const e = { id: uuid(), createdAt: now, updatedAt: now, ...expense }
    setState(s => ({ ...s, expenses: [e, ...s.expenses] }))
    if (session) {
      try { await api.createExpense(e) } catch (err) { console.warn('createExpense', err) }
    }
    return e
  }, [session])

  const updateExpense = useCallback(async (id, patch) => {
    const now = new Date().toISOString()
    setState(s => ({
      ...s,
      expenses: s.expenses.map(e => e.id === id ? { ...e, ...patch, updatedAt: now } : e),
    }))
    if (session) {
      try { await api.patchExpense(id, { ...patch, updatedAt: now }) } catch (err) { console.warn('patchExpense', err) }
    }
  }, [session])

  const removeExpense = useCallback(async (id) => {
    setState(s => ({ ...s, expenses: s.expenses.filter(e => e.id !== id) }))
    if (session) {
      try { await api.deleteExpense(id) } catch (err) { console.warn('deleteExpense', err) }
    }
  }, [session])

  const addSettlement = useCallback(async (s) => {
    const now = new Date().toISOString()
    const obj = { id: uuid(), createdAt: now, updatedAt: now, ...s }
    setState(prev => ({ ...prev, settlements: [obj, ...prev.settlements] }))
    if (session) {
      try { await api.createSettlement(obj) } catch (err) { console.warn('createSettlement', err) }
    }
    return obj
  }, [session])

  const updateSettlement = useCallback(async (id, patch) => {
    const now = new Date().toISOString()
    setState(s => ({
      ...s,
      settlements: s.settlements.map(x => x.id === id ? { ...x, ...patch, updatedAt: now } : x),
    }))
    if (session) {
      try { await api.patchSettlement(id, { ...patch, updatedAt: now }) } catch (err) { console.warn('patchSettlement', err) }
    }
  }, [session])

  const removeSettlement = useCallback(async (id) => {
    setState(s => ({ ...s, settlements: s.settlements.filter(x => x.id !== id) }))
    if (session) {
      try { await api.deleteSettlement(id) } catch (err) { console.warn('deleteSettlement', err) }
    }
  }, [session])

  // Opzione personale per-gruppo: quota su Tally. Ritorna la response del
  // server ({ pending } serve al prompt di backfill) o null se offline/errore.
  const setGroupTallySync = useCallback(async (groupId, enabled) => {
    setState(s => {
      const next = { ...(s.tallySync ?? {}) }
      if (enabled) next[groupId] = true
      else delete next[groupId]
      return { ...s, tallySync: next }
    })
    if (!session) return null
    try { return await api.setTallySync(groupId, enabled) }
    catch (e) { console.warn('setTallySync', e); return null }
  }, [session])

  const backfillGroupTally = useCallback(async (groupId) => {
    if (!session) return null
    try { return await api.backfillTallySync(groupId) }
    catch (e) { console.warn('backfillTallySync', e); return null }
  }, [session])

  const wipeGroupTally = useCallback(async (groupId) => {
    if (!session) return null
    try { return await api.wipeTallySync(groupId) }
    catch (e) { console.warn('wipeTallySync', e); return null }
  }, [session])

  const setExpenseTallyExcluded = useCallback(async (expenseId, excluded) => {
    setState(s => {
      const next = { ...(s.tallyExcluded ?? {}) }
      if (excluded) next[expenseId] = true
      else delete next[expenseId]
      return { ...s, tallyExcluded: next }
    })
    if (!session) return
    try { await api.setExpenseTallyExcluded(expenseId, excluded) }
    catch (e) { console.warn('setExpenseTallyExcluded', e) }
  }, [session])

  const resetAll = useCallback(async () => {
    setState({ ...EMPTY, currency: state.currency })
    if (session) {
      try { await api.replaceAllData({ groups: [], expenses: [], settlements: [] }) }
      catch (e) { console.warn('resetAll', e) }
    }
  }, [session, state.currency])

  const importJson = useCallback(async (payload) => {
    const next = {
      currency: payload.currency || state.currency || 'EUR',
      groups: payload.groups ?? [],
      expenses: payload.expenses ?? [],
      settlements: payload.settlements ?? [],
    }
    setState(next)
    if (session) {
      try {
        if (next.currency) await api.saveSettings({ currency: next.currency })
        await api.replaceAllData(next)
      } catch (e) { console.warn('import', e) }
    }
  }, [session, state.currency])

  const value = {
    ...state,
    loading,
    error,
    refresh,
    setCurrency,
    setDateFormat,
    createGroup,
    updateGroup,
    removeGroup,
    addExpense,
    updateExpense,
    removeExpense,
    addSettlement,
    updateSettlement,
    removeSettlement,
    setGroupTallySync,
    backfillGroupTally,
    wipeGroupTally,
    setExpenseTallyExcluded,
    resetAll,
    importJson,
  }

  return <EvenCtx.Provider value={value}>{children}</EvenCtx.Provider>
}

export function useEven() {
  const ctx = useContext(EvenCtx)
  if (!ctx) throw new Error('useEven outside EvenProvider')
  return ctx
}
